// src/pages/MyResultsPage.tsx
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { Button } from '../components/common/Button';
import { getMyResults, getResultDetail, createConclusion } from '../api/results';
import type { ResultDetail, QuestionAnswer } from '../api/types';
import { Result } from '../types/testData';

const PageContainer = styled.div`
  min-height: 100vh;
  padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.lg}`};
  background: ${({ theme }) => theme.colors.background};
  box-sizing: border-box;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  flex: 1;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 14px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textLight};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 0.88rem;
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: middle;
`;

const Tr = styled.tr<{ $expanded?: boolean }>`
  cursor: pointer;
  &:hover td { background: ${({ theme }) => theme.colors.background}; }
  ${({ $expanded, theme }) => $expanded && `td { background: ${theme.colors.background}; }`}
`;

const DetailRow = styled.tr`
  background: ${({ theme }) => theme.colors.background};
`;

const DetailTd = styled.td`
  padding: 0;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
`;

const DetailTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const DetailTh = styled.th`
  text-align: left;
  padding: 8px 14px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textLight};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.cardBackground};
`;

const DetailCell = styled.td<{ $muted?: boolean }>`
  padding: 7px 14px;
  font-size: 0.85rem;
  color: ${({ $muted, theme }) => $muted ? theme.colors.textLight : theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:last-child { border-bottom: none; }
`;

const CodeBadge = styled.span`
  font-family: monospace;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.15em;
`;

/* ── Toast ── */
const ToastMsg = styled.div`
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  padding: 12px 24px;
  border-radius: 8px;
  background: #333;
  color: #fff;
  font-size: 0.9rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  animation: fadeInUp 0.3s ease-out;
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateX(-50%) translateY(12px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
`;

const SKIP_LABELS: Record<string, string> = {
  'пропуск': 'пропуск',
  'закончилось время': '⏱ время',
};

const MyResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [expanded, setExpanded] = useState<Record<number, ResultDetail>>({});
  const [loadingDetail, setLoadingDetail] = useState<Record<number, boolean>>({});
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [requestingId, setRequestingId] = useState<number | null>(null);
  const [requestedIds, setRequestedIds] = useState<Set<number>>(new Set());
  const [toastMsg, setToastMsg] = useState('');

  // Auto-dismiss toast
  useEffect(() => {
    if (!toastMsg) return;
    const timer = window.setTimeout(() => setToastMsg(''), 3500);
    return () => window.clearTimeout(timer);
  }, [toastMsg]);

  useEffect(() => {
    getMyResults()
      .then(setResults)
      .catch(() => setLoadError(t('resultsLoadError')))
      .finally(() => setLoading(false));
  }, []);

  const toggleDetail = async (id: number) => {
    if (expanded[id]) {
      setExpanded(prev => { const n = { ...prev }; delete n[id]; return n; });
      return;
    }
    setLoadingDetail(prev => ({ ...prev, [id]: true }));
    try {
      const detail = await getResultDetail(id);
      setExpanded(prev => ({ ...prev, [id]: detail }));
    } catch {
      // ignore
    } finally {
      setLoadingDetail(prev => ({ ...prev, [id]: false }));
    }
  };

  const requestConclusion = async (id: number) => {
    setRequestingId(id);
    try {
      await createConclusion([id], 'individual');
      setRequestedIds(prev => new Set(Array.from(prev).concat(id)));
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setToastMsg(detail || t('conclusionRequestError'));
    } finally {
      setRequestingId(null);
    }
  };

  const copyCode = (id: number, code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const colCount = 6;

  return (
    <PageContainer>
      <PageHeader>
        <Button variant="outline" size="sm" onClick={() => navigate('/tests')}>{t('backBtn')}</Button>
        <PageTitle>{t('myResultsTitle')}</PageTitle>
      </PageHeader>

      {loading && <p style={{ color: '#888' }}>{t('genLoading')}</p>}
      {!loading && loadError && <p style={{ color: '#c0392b' }}>{loadError}</p>}
      {!loading && !loadError && results.length === 0 && <p style={{ color: '#888' }}>{t('myResultsEmpty')}</p>}

      {!loading && !loadError && results.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th>{t('myResultsColTest')}</Th>
              <Th>{t('myResultsColDate')}</Th>
              <Th>{t('myResultsColTime')}</Th>
              <Th>{t('myResultsColCompare')}</Th>
              <Th>{t('conclusionTitle')}</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {results.map(r => (
              <React.Fragment key={r.id}>
                <Tr $expanded={!!expanded[r.id]} onClick={() => toggleDetail(r.id)}>
                  <Td>{r.test_title || `Тест #${r.test_id}`}</Td>
                  <Td style={{ whiteSpace: 'nowrap' }}>
                    {r.completed_at ? new Date(r.completed_at).toLocaleString() : '—'}
                  </Td>
                  <Td>{r.time_spent ?? '—'}</Td>
                  <Td onClick={e => e.stopPropagation()}>
                    {r.share_code ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CodeBadge>{r.share_code}</CodeBadge>
                        <Button variant="outline" size="sm" onClick={() => copyCode(r.id, r.share_code!)}>
                          {copiedId === r.id ? t('shareCodeCopied') : t('shareCodeCopy')}
                        </Button>
                      </div>
                    ) : '—'}
                  </Td>
                  <Td onClick={e => e.stopPropagation()}>
                    {r.conclusion_exists && r.conclusion_id ? (
                      <a
                        href={`/conclusion/${r.conclusion_id}`}
                        onClick={e => {
                          e.preventDefault();
                          navigate(`/conclusion/${r.conclusion_id}`);
                        }}
                        style={{ color: '#38a169', fontSize: '0.85rem', textDecoration: 'none' }}
                      >
                        {t('conclusionExistsLink')}
                      </a>
                    ) : requestedIds.has(r.id) ? (
                      <span style={{ color: '#38a169', fontSize: '0.85rem' }}>{t('conclusionRequested')}</span>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={requestingId === r.id}
                        onClick={() => requestConclusion(r.id)}
                      >
                        {requestingId === r.id ? t('genLoading') : t('conclusionRequestBtn')}
                      </Button>
                    )}
                  </Td>
                  <Td style={{ color: '#888', fontSize: '0.8rem', textAlign: 'center' }}>
                    {loadingDetail[r.id] ? '...' : expanded[r.id] ? '▲' : '▼'}
                  </Td>
                </Tr>

                {expanded[r.id] && (
                  <DetailRow>
                    <DetailTd colSpan={colCount}>
                      <DetailTable>
                        <thead>
                          <tr>
                            <DetailTh style={{ width: '3%' }}>{t('detailColN')}</DetailTh>
                            <DetailTh style={{ width: '47%' }}>{t('myResultsColQuestion')}</DetailTh>
                            <DetailTh>{t('myResultsColAnswer')}</DetailTh>
                            <DetailTh style={{ width: '10%' }}>{t('myResultsColTimeMs')}</DetailTh>
                          </tr>
                        </thead>
                        <tbody>
                          {(expanded[r.id].answers || []).map((a: QuestionAnswer, i: number) => (
                            <tr key={a.question_id}>
                              <DetailCell $muted>{a.question_order ?? i + 1}</DetailCell>
                              <DetailCell>{a.question_content}</DetailCell>
                              <DetailCell>
                                {a.skip_reason
                                  ? <span style={{ color: '#f59e0b' }}>{SKIP_LABELS[a.skip_reason] || a.skip_reason}</span>
                                  : a.custom_answer
                                    ? <span style={{ color: '#555' }}>{a.custom_answer}</span>
                                    : a.option_content || <span style={{ color: '#bbb' }}>—</span>
                                }
                              </DetailCell>
                              <DetailCell $muted>{a.question_time ?? '—'}</DetailCell>
                            </tr>
                          ))}
                        </tbody>
                      </DetailTable>
                    </DetailTd>
                  </DetailRow>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      )}

      {/* ── Toast ── */}
      {toastMsg && <ToastMsg>{toastMsg}</ToastMsg>}
    </PageContainer>
  );
};

export default MyResultsPage;
